import React, { useState, useMemo } from 'react';
import { useApp } from '../context/AppContext';
import { QUOTE_STATUSES, calculateQuoteTotals, MOCK_PRODUCTS } from '../data/mockData';
import { ResizableHandle, ResizablePanel, ResizablePanelGroup } from './ui/resizable';
import { TreeBuilder } from './TreeBuilder';
import { DetailPanel } from './DetailPanel';
import { ImportScheduleWizard } from './ImportScheduleWizard';
import { 
  Calendar, 
  Building2, 
  ChevronLeft,
  Wand2,
  FileText,
  Clock,
  CheckCircle2,
  Package,
  Copy,
  DollarSign,
  TrendingDown,
  TrendingUp,
  Star,
  Printer,
  Search,
  DoorOpen,
  ArrowRight,
  Upload,
} from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Badge } from './ui/badge';
import { ScrollArea } from './ui/scroll-area';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from './ui/select';
import { toast } from 'sonner';
import { cn } from '../lib/utils';

export function SchedulingModule() {
  const { state, actions } = useApp();
  const { quotes, currentQuote, opportunities } = state;

  // Filter quotes based on current context
  const availableQuotes = quotes;

  // Get all versions for current builder
  const builderQuotes = useMemo(() => {
    if (!currentQuote) return [];
    return quotes
      .filter(q => q.builderId === currentQuote.builderId)
      .sort((a, b) => (a.version || 1) - (b.version || 1));
  }, [quotes, currentQuote]);

  // Calculate totals for current quote
  const quoteTotals = useMemo(() => {
    return calculateQuoteTotals(currentQuote);
  }, [currentQuote]);

  // Calculate previous version totals for comparison
  const previousVersionTotals = useMemo(() => {
    if (!currentQuote || builderQuotes.length < 2) return null;
    const currentVersion = currentQuote.version || 1;
    const previousQuote = builderQuotes.find(q => (q.version || 1) === currentVersion - 1);
    return previousQuote ? calculateQuoteTotals(previousQuote) : null;
  }, [currentQuote, builderQuotes]);

  // Cost difference calculation
  const costDifference = useMemo(() => {
    if (!previousVersionTotals || previousVersionTotals.totalCost === 0) return null;
    const diff = quoteTotals.totalCost - previousVersionTotals.totalCost;
    const percent = ((diff / previousVersionTotals.totalCost) * 100).toFixed(1);
    return { diff, percent, isLower: diff < 0 };
  }, [quoteTotals, previousVersionTotals]);

  const handleSelectQuote = (quoteId) => {
    actions.setCurrentQuote(quoteId);
  };

  const handleBackToCRM = () => {
    actions.setActiveModule('crm');
  };

  const handleAutoBuild = () => {
    actions.autoBuildSchedule();
    toast.success('Schedule auto-built with doors and hardware assigned');
  };

  const handleDuplicateQuote = () => {
    actions.duplicateQuote();
    toast.success('Quote duplicated - you can now edit this new version');
  };

  const handleSetActive = () => {
    actions.setQuoteActive(currentQuote.id);
    toast.success('Quote marked as active/sent');
  };

  const handleVersionChange = (quoteId) => {
    actions.setCurrentQuote(quoteId);
  };

  const handleGenerateQuote = () => {
    if (!currentQuote) return;
    const opportunity = opportunities.find(o => o.id === currentQuote.opportunityId);
    const builder = opportunity?.builders.find(b => b.id === currentQuote.builderId);
    const totals = calculateQuoteTotals(currentQuote);

    // Aggregate line items (group products by id, count occurrences)
    const productCounts = {};
    (currentQuote.buildings || []).forEach(building => {
      (building.floors || []).forEach(floor => {
        (floor.doors || []).forEach(door => {
          (door.products || []).forEach(productId => {
            productCounts[productId] = (productCounts[productId] || 0) + 1;
          });
        });
      });
    });
    const lineItems = Object.entries(productCounts)
      .map(([id, qty]) => {
        const p = MOCK_PRODUCTS.find(x => x.id === id);
        if (!p) return null;
        return { name: p.name, type: p.type, qty, unitPrice: p.price, total: p.price * qty };
      })
      .filter(Boolean)
      .sort((a, b) => a.type.localeCompare(b.type));

    const today = new Date().toLocaleDateString('en-AU', { day: '2-digit', month: 'long', year: 'numeric' });
    const shortId = currentQuote.id.slice(-6).toUpperCase();

    const rows = lineItems.map(item =>
      `<tr>
        <td>${item.name}</td>
        <td style="text-align:center">${item.qty}</td>
        <td style="text-align:right">$${item.unitPrice.toFixed(2)}</td>
        <td style="text-align:right">$${item.total.toFixed(2)}</td>
      </tr>`
    ).join('');

    const buildingRows = (currentQuote.buildings || []).map(b => {
      const doors = b.floors.reduce((s, f) => s + f.doors.length, 0);
      return `<tr><td>${b.name}</td><td style="text-align:center">${b.floors.length}</td><td style="text-align:center">${doors}</td></tr>`;
    }).join('');

    const html = `<!DOCTYPE html>
<html>
<head>
<meta charset="utf-8"/>
<title>Quote – ${currentQuote.name}</title>
<style>
  body { font-family: Arial, sans-serif; font-size: 13px; color: #1a1a1a; margin: 40px; }
  h1 { font-size: 22px; margin-bottom: 4px; }
  .subtitle { color: #555; font-size: 13px; margin-bottom: 24px; }
  .meta-grid { display: grid; grid-template-columns: 1fr 1fr; gap: 4px 32px; margin-bottom: 24px; }
  .meta-grid .label { color: #666; font-size: 12px; }
  .meta-grid .value { font-weight: 600; font-size: 13px; }
  h2 { font-size: 14px; text-transform: uppercase; letter-spacing: 0.05em; color: #444; border-bottom: 1px solid #ddd; padding-bottom: 4px; margin: 24px 0 10px; }
  table { width: 100%; border-collapse: collapse; font-size: 13px; }
  th { text-align: left; background: #f4f4f4; padding: 6px 8px; font-size: 12px; text-transform: uppercase; letter-spacing: 0.04em; border-bottom: 2px solid #ddd; }
  td { padding: 6px 8px; border-bottom: 1px solid #eee; }
  .total-row td { font-weight: 700; background: #f9f9f9; border-top: 2px solid #ccc; }
  .footer { margin-top: 40px; font-size: 11px; color: #999; text-align: center; border-top: 1px solid #eee; padding-top: 12px; }
  @media print { body { margin: 20mm; } }
</style>
</head>
<body>
  <h1>ACCESS HARDWARE — QUOTE</h1>
  <div class="subtitle">Door Hardware Schedule &amp; Specification</div>

  <div class="meta-grid">
    <div><div class="label">Project</div><div class="value">${opportunity?.name || 'Unknown Project'}</div></div>
    <div><div class="label">Quote Number</div><div class="value">QT-${shortId}</div></div>
    <div><div class="label">Builder</div><div class="value">${builder?.name || 'Unknown Builder'}</div></div>
    <div><div class="label">Date</div><div class="value">${today}</div></div>
    <div><div class="label">Version</div><div class="value">v${currentQuote.version || 1}</div></div>
    <div><div class="label">Developer</div><div class="value">${opportunity?.developer || '—'}</div></div>
  </div>

  <h2>Door Schedule Summary</h2>
  <table>
    <thead><tr><th>Building</th><th style="text-align:center">Floors</th><th style="text-align:center">Doors</th></tr></thead>
    <tbody>
      ${buildingRows || '<tr><td colspan="3" style="color:#999">No buildings in this quote</td></tr>'}
      <tr class="total-row">
        <td>Total</td>
        <td style="text-align:center">${currentQuote.buildings.reduce((s, b) => s + b.floors.length, 0)}</td>
        <td style="text-align:center">${totals.doorCount}</td>
      </tr>
    </tbody>
  </table>

  <h2>Line Items</h2>
  <table>
    <thead><tr><th>Product</th><th style="text-align:center">Qty</th><th style="text-align:right">Unit Price</th><th style="text-align:right">Total</th></tr></thead>
    <tbody>
      ${rows || '<tr><td colspan="4" style="color:#999">No products assigned to doors</td></tr>'}
      <tr class="total-row">
        <td colspan="3">TOTAL</td>
        <td style="text-align:right">$${totals.totalCost.toFixed(2)}</td>
      </tr>
    </tbody>
  </table>

  <div class="footer">Generated by Access Hardware Door Scheduler &nbsp;|&nbsp; ${today}</div>
</body>
</html>`;

    const printWindow = window.open('', '_blank', 'width=900,height=700');
    if (!printWindow) { toast.error('Please allow pop-ups to generate the quote'); return; }
    printWindow.document.write(html);
    printWindow.document.close();
    printWindow.focus();
    setTimeout(() => { printWindow.print(); }, 300);
  };

  const [quoteSearch, setQuoteSearch] = useState('');
  const [quoteStatusFilter, setQuoteStatusFilter] = useState('all');
  const [showImportWizard, setShowImportWizard] = useState(false);

  if (!currentQuote) {
    const activeCount = availableQuotes.filter(q => q.status === 'active').length;

    const filteredQuotes = availableQuotes.filter(q => {
      const opp = opportunities.find(o => o.id === q.opportunityId);
      const builder = opp?.builders.find(b => b.id === q.builderId);
      const search = quoteSearch.toLowerCase();
      const matchesSearch = !search ||
        (opp?.name || '').toLowerCase().includes(search) ||
        (builder?.name || '').toLowerCase().includes(search) ||
        q.name.toLowerCase().includes(search);
      const matchesStatus = quoteStatusFilter === 'all' || q.status === quoteStatusFilter;
      return matchesSearch && matchesStatus;
    });

    // Group filtered quotes by opportunity
    const groups = filteredQuotes.reduce((acc, q) => {
      const oppId = q.opportunityId || '__none__';
      if (!acc[oppId]) acc[oppId] = { opp: opportunities.find(o => o.id === q.opportunityId) || null, quotes: [] };
      acc[oppId].quotes.push(q);
      return acc;
    }, {});

    return (
      <div className="h-full flex flex-col" data-testid="scheduling-module">
        {/* Header */}
        <div className="h-14 border-b border-border flex items-center px-4 justify-between bg-white shrink-0">
          <div className="flex items-center gap-3">
            <Calendar className="w-5 h-5 text-primary" />
            <h1 className="font-semibold text-lg">Door Scheduling</h1>
          </div>
          <Button variant="ghost" size="sm" onClick={handleBackToCRM}>
            <ChevronLeft className="w-4 h-4 mr-1" />
            Back to CRM
          </Button>
        </div>

        {availableQuotes.length === 0 ? (
          <div className="flex-1 flex items-center justify-center bg-muted/30">
            <div className="text-center max-w-md">
              <FileText className="w-12 h-12 mx-auto mb-4 text-muted-foreground/50" />
              <h2 className="text-xl font-semibold mb-2">No Quotes Available</h2>
              <p className="text-muted-foreground mb-4">
                Create a quote from the CRM module by selecting an opportunity and builder.
              </p>
              <Button onClick={handleBackToCRM} data-testid="go-to-crm-btn">
                <ChevronLeft className="w-4 h-4 mr-2" />
                Go to CRM
              </Button>
            </div>
          </div>
        ) : (
          <div className="flex-1 flex overflow-hidden bg-muted/20">

            {/* Left sidebar */}
            <div className="w-64 shrink-0 border-r border-border bg-white flex flex-col">
              <div className="p-4 border-b border-border">
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                  <Input
                    placeholder="Search quotes…"
                    value={quoteSearch}
                    onChange={e => setQuoteSearch(e.target.value)}
                    className="pl-9 h-8 text-sm"
                  />
                </div>
              </div>

              {/* Status filter */}
              <div className="p-3 border-b border-border space-y-1">
                <p className="text-xs font-semibold uppercase tracking-wider text-muted-foreground px-1 mb-2">Filter by Status</p>
                {[
                  { id: 'all', label: 'All Quotes', count: availableQuotes.length },
                  { id: 'draft', label: 'Draft', count: availableQuotes.filter(q => q.status === 'draft').length },
                  { id: 'submitted', label: 'Submitted', count: availableQuotes.filter(q => q.status === 'submitted').length },
                  { id: 'active', label: 'Active Schedule', count: activeCount },
                ].map(f => (
                  <button
                    key={f.id}
                    onClick={() => setQuoteStatusFilter(f.id)}
                    className={cn(
                      'w-full flex items-center justify-between px-3 py-1.5 rounded-md text-sm transition-colors',
                      quoteStatusFilter === f.id
                        ? 'bg-primary text-primary-foreground'
                        : 'hover:bg-muted text-foreground'
                    )}
                  >
                    <span>{f.label}</span>
                    <span className={cn(
                      'text-xs font-medium px-1.5 py-0.5 rounded-full',
                      quoteStatusFilter === f.id ? 'bg-primary-foreground/20 text-primary-foreground' : 'bg-muted text-muted-foreground'
                    )}>{f.count}</span>
                  </button>
                ))}
              </div>

              {/* Summary stats */}
              <div className="mt-auto p-4 border-t border-border space-y-2">
                <div className="flex items-center justify-between text-sm">
                  <span className="text-muted-foreground">Total quotes</span>
                  <span className="font-semibold">{availableQuotes.length}</span>
                </div>
                <div className="flex items-center justify-between text-sm">
                  <span className="text-muted-foreground">Active schedules</span>
                  <span className={cn('font-semibold', activeCount > 0 ? 'text-green-600' : 'text-muted-foreground')}>{activeCount}</span>
                </div>
                <div className="flex items-center justify-between text-sm">
                  <span className="text-muted-foreground">Projects</span>
                  <span className="font-semibold">{new Set(availableQuotes.map(q => q.opportunityId)).size}</span>
                </div>
              </div>
            </div>

            {/* Right panel — grouped by opportunity */}
            <ScrollArea className="flex-1">
              <div className="p-6 space-y-6">
                {Object.keys(groups).length === 0 ? (
                  <div className="text-center py-16 text-muted-foreground">
                    <Search className="w-8 h-8 mx-auto mb-2 opacity-40" />
                    <p className="text-sm">No quotes match your search</p>
                  </div>
                ) : (
                  Object.entries(groups).map(([oppId, { opp, quotes: groupQuotes }]) => (
                    <div key={oppId}>
                      {/* Opportunity group header */}
                      <div className="flex items-center gap-3 mb-3">
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2">
                            <h2 className="text-sm font-semibold truncate">
                              {opp?.name || 'Unknown Opportunity'}
                            </h2>
                            {opp?.developer && (
                              <span className="text-xs text-muted-foreground truncate">— {opp.developer}</span>
                            )}
                          </div>
                        </div>
                        <span className="text-xs text-muted-foreground shrink-0">{groupQuotes.length} quote{groupQuotes.length !== 1 ? 's' : ''}</span>
                      </div>

                      <div className="space-y-2">
                        {groupQuotes.map(quote => (
                          <QuoteCard
                            key={quote.id}
                            quote={quote}
                            opportunity={opp}
                            onClick={() => handleSelectQuote(quote.id)}
                          />
                        ))}
                      </div>
                    </div>
                  ))
                )}
              </div>
            </ScrollArea>
          </div>
        )}
      </div>
    );
  }

  const isActiveSchedule = currentQuote.status === 'active';
  const isActiveQuote = currentQuote.isActive;

  return (
    <div className="h-full flex flex-col" data-testid="scheduling-module">
      {/* Header */}
      <div className="h-14 border-b border-border flex items-center px-4 justify-between bg-white shrink-0">
        <div className="flex items-center gap-3">
          <Button variant="ghost" size="sm" onClick={() => actions.setCurrentQuote(null)}>
            <ChevronLeft className="w-4 h-4" />
          </Button>
          <div className="flex items-center gap-2">
            {isActiveSchedule ? (
              <CheckCircle2 className="w-5 h-5 text-green-600" />
            ) : (
              <FileText className="w-5 h-5 text-primary" />
            )}
            <div>
              <h1 className="font-semibold text-sm">{currentQuote.name}</h1>
              <p className="text-xs text-muted-foreground">
                {isActiveSchedule ? 'Active Schedule' : 'Draft Quote'}
              </p>
            </div>
          </div>
          <QuoteStatusBadge status={currentQuote.status} />
          {isActiveQuote && (
            <Badge className="bg-amber-100 text-amber-700 ml-1">
              <Star className="w-3 h-3 mr-1 fill-current" />
              Active
            </Badge>
          )}
        </div>

        <div className="flex items-center gap-2">
          {/* Version Selector */}
          {builderQuotes.length > 1 && (
            <Select value={currentQuote.id} onValueChange={handleVersionChange}>
              <SelectTrigger className="w-32" data-testid="version-selector">
                <SelectValue>v{currentQuote.version || 1}</SelectValue>
              </SelectTrigger>
              <SelectContent>
                {builderQuotes.map(q => (
                  <SelectItem key={q.id} value={q.id}>
                    <div className="flex items-center gap-2">
                      <span>v{q.version || 1}</span>
                      {q.isActive && <Star className="w-3 h-3 text-amber-500 fill-current" />}
                    </div>
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          )}

          {currentQuote.buildings.length === 0 && (
            <Button onClick={handleAutoBuild} data-testid="auto-build-btn">
              <Wand2 className="w-4 h-4 mr-2" />
              Auto Build Schedule
            </Button>
          )}

          {/* Generate Quote PDF */}
          <Button variant="outline" onClick={handleGenerateQuote} data-testid="generate-quote-btn" title="Open print-ready quote">
            <Printer className="w-4 h-4 mr-2" />
            Generate Quote
          </Button>

          {/* Import Schedule */}
          <Button variant="outline" onClick={() => setShowImportWizard(true)} data-testid="import-schedule-btn" title="Import door schedule from CSV">
            <Upload className="w-4 h-4 mr-2" />
            Import CSV
          </Button>

          {/* Duplicate Quote */}
          <Button variant="outline" onClick={handleDuplicateQuote} data-testid="duplicate-quote-btn">
            <Copy className="w-4 h-4 mr-2" />
            Duplicate Quote
          </Button>

          {/* Mark as Active */}
          {!isActiveQuote && !isActiveSchedule && (
            <Button variant="outline" onClick={handleSetActive} data-testid="mark-active-btn">
              <Star className="w-4 h-4 mr-2" />
              Mark as Active
            </Button>
          )}

          {isActiveSchedule && (
            <Button 
              variant="outline" 
              onClick={() => actions.setActiveModule('deliveries')}
              data-testid="go-to-deliveries-btn"
            >
              <Package className="w-4 h-4 mr-2" />
              Manage Deliveries
            </Button>
          )}
        </div>
      </div>

      {/* Cost Summary Bar */}
      <div className="h-12 border-b border-border flex items-center px-4 justify-between bg-slate-50 shrink-0">
        <div className="flex items-center gap-6">
          <div className="flex items-center gap-2">
            <Building2 className="w-4 h-4 text-muted-foreground" />
            <span className="text-sm">
              <span className="font-medium">{currentQuote.buildings.length}</span>
              <span className="text-muted-foreground ml-1">Buildings</span>
            </span>
          </div>
          <div className="flex items-center gap-2">
            <Package className="w-4 h-4 text-muted-foreground" />
            <span className="text-sm">
              <span className="font-medium">{quoteTotals.doorCount}</span>
              <span className="text-muted-foreground ml-1">Doors</span>
            </span>
          </div>
          <div className="flex items-center gap-2">
            <CheckCircle2 className="w-4 h-4 text-muted-foreground" />
            <span className="text-sm">
              <span className="font-medium">{quoteTotals.totalItems}</span>
              <span className="text-muted-foreground ml-1">Items</span>
            </span>
          </div>
        </div>

        <div className="flex items-center gap-4">
          {/* Cost Comparison */}
          {costDifference && (
            <div className={cn(
              "flex items-center gap-1 text-sm px-2 py-1 rounded",
              costDifference.isLower ? "bg-green-100 text-green-700" : "bg-red-100 text-red-700"
            )}>
              {costDifference.isLower ? (
                <TrendingDown className="w-4 h-4" />
              ) : (
                <TrendingUp className="w-4 h-4" />
              )}
              <span className="font-medium">
                {costDifference.isLower ? '↓' : '↑'} {Math.abs(parseFloat(costDifference.percent))}%
              </span>
              <span className="text-xs opacity-75">vs previous</span>
            </div>
          )}

          {/* Total Cost */}
          <div className="flex items-center gap-2 bg-primary/10 px-3 py-1.5 rounded-lg" data-testid="total-cost">
            <DollarSign className="w-4 h-4 text-primary" />
            <span className="text-sm text-muted-foreground">Total:</span>
            <span className="text-lg font-semibold text-primary">
              ${quoteTotals.totalCost.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
            </span>
          </div>
        </div>
      </div>

      {/* Main Content - Split Panel or Import Wizard */}
      <div className="flex-1 overflow-hidden">
        {showImportWizard ? (
          <ImportScheduleWizard onClose={() => { setShowImportWizard(false); toast.success('Schedule imported successfully'); }} />
        ) : (
        <ResizablePanelGroup direction="horizontal" className="h-full">
          <ResizablePanel defaultSize={30} minSize={20} maxSize={45}>
            <TreeBuilder />
          </ResizablePanel>
          <ResizableHandle withHandle className="bg-border" />
          <ResizablePanel defaultSize={70}>
            <DetailPanel />
          </ResizablePanel>
        </ResizablePanelGroup>
        )}
      </div>
    </div>
  );
}

function QuoteCard({ quote, opportunity, onClick }) {
  const isActiveSchedule = quote.status === 'active';
  const isActiveQuote = quote.isActive;
  const totals = calculateQuoteTotals(quote);
  const doorCount = totals.doorCount;
  const buildingCount = quote.buildings?.length || 0;
  const builder = opportunity?.builders?.find(b => b.id === quote.builderId);

  return (
    <div
      className={cn(
        "border rounded-lg cursor-pointer transition-all hover:shadow-sm hover:border-primary/40 bg-white overflow-hidden",
        isActiveSchedule ? "border-green-300 border-l-4 border-l-green-500" :
        isActiveQuote ? "border-amber-300 border-l-4 border-l-amber-400" :
        "border-border"
      )}
      onClick={onClick}
      data-testid={`quote-card-${quote.id}`}
    >
      <div className="p-4">
        {/* Top row: builder name + status */}
        <div className="flex items-start justify-between gap-3">
          <div className="flex items-center gap-2 min-w-0">
            <FileText className={cn(
              'w-4 h-4 shrink-0',
              isActiveSchedule ? 'text-green-600' : 'text-muted-foreground'
            )} />
            <span className="font-semibold text-sm truncate">
              {builder?.name || 'Unknown Builder'}
            </span>
            {(quote.version || 1) > 1 && (
              <Badge variant="outline" className="text-xs shrink-0">v{quote.version}</Badge>
            )}
            {isActiveQuote && (
              <Badge className="bg-amber-100 text-amber-700 text-xs shrink-0">
                <Star className="w-2.5 h-2.5 mr-0.5 fill-current" />
                Active
              </Badge>
            )}
          </div>
          <div className="flex items-center gap-2 shrink-0">
            <QuoteStatusBadge status={quote.status} />
            <span className="text-sm font-semibold text-primary">
              ${totals.totalCost.toLocaleString()}
            </span>
          </div>
        </div>

        {/* Divider */}
        <div className="mt-3 mb-2 border-t border-border/50" />

        {/* Stats row */}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4 text-xs text-muted-foreground">
            <span className="flex items-center gap-1">
              <Building2 className="w-3 h-3" />
              <span className="font-medium text-foreground">{buildingCount}</span> buildings
            </span>
            <span className="flex items-center gap-1">
              <DoorOpen className="w-3 h-3" />
              <span className="font-medium text-foreground">{doorCount}</span> doors
            </span>
            <span className="flex items-center gap-1">
              <Package className="w-3 h-3" />
              <span className="font-medium text-foreground">{totals.totalItems}</span> items
            </span>
            <span className="flex items-center gap-1">
              <Clock className="w-3 h-3" />
              {new Date(quote.createdAt).toLocaleDateString('en-AU', { day: '2-digit', month: 'short', year: '2-digit' })}
            </span>
          </div>
          <ArrowRight className="w-4 h-4 text-muted-foreground/50" />
        </div>
      </div>
    </div>
  );
}

function QuoteStatusBadge({ status }) {
  const statusConfig = QUOTE_STATUSES.find(s => s.id === status);
  
  const colorClasses = {
    slate: 'bg-slate-100 text-slate-700',
    blue: 'bg-blue-100 text-blue-700',
    green: 'bg-green-100 text-green-700'
  };
  
  return (
    <Badge className={colorClasses[statusConfig?.color || 'slate']}>
      {status === 'active' && <CheckCircle2 className="w-3 h-3 mr-1" />}
      {statusConfig?.name || status}
    </Badge>
  );
}
